import Link from '@/lib/compat/link';
import { usePathname } from '@/lib/compat/navigation';
import { cn } from '@/lib/utils';
import {
  MoreHorizontal,
  Copy,
  ExternalLink,
  User,
  Shield,
  LogOut,
  Globe,
  Sun,
  Moon,
  Monitor,
  ChevronDown,
} from 'lucide-react';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Badge } from '@/components/ui/badge';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuGroup,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { useAuth } from '@/lib/auth/auth-context';
import {
  getTopNavMenuItems,
  isMainNavActive,
  isSidebarItemActive,
  mainNavItems,
  moreMenuItems,
} from '@/config/navigation';
import { useTranslations } from '@/lib/i18n/client';
import { usePermissions } from '@/lib/hooks/use-permissions';
import { useFilteredNavSections } from '@/lib/hooks/use-filtered-nav-sections';
import { useEffect, useState } from 'react';
import { getClient } from '@/lib/graphql/client';
import { GET_CURRENT_TENANT } from '@/lib/graphql/operations/settings';
import { resolveStoreIndicator, type StoreIndicatorTenant } from '@/lib/tenant/store-indicator';
import { useOrderActionCounts } from '@/lib/hooks/use-order-action-counts';
import { toast } from 'sonner';
import { NotificationBell } from './notification-bell';
import { DASHBOARD_LANGUAGE_STORAGE_KEY } from '@/lib/i18n/dashboard-language';
import { useTheme } from '@/lib/theme/theme-provider';
import { TaliLogo } from '@/components/brand/tali-logo';
import { GlobalCommandMenu } from './global-command-menu';

export function TopNav() {
  const pathname = usePathname();
  const { user, activeChannel, logout } = useAuth();
  const t = useTranslations();
  const { hasPermission } = usePermissions();
  const { ordersBadge } = useOrderActionCounts();
  const { mode, setMode } = useTheme();
  const visibleDirectJumpSections = useFilteredNavSections(
    mainNavItems.map(item => ({ items: getTopNavMenuItems(item.href) })),
  );
  const visibleDirectJumpHrefs = new Set(
    (visibleDirectJumpSections ?? []).flatMap(section => section.items.map(item => item.href)),
  );

  const visibleNavItems = mainNavItems.filter(
    (i) => !i.requiredPermission || hasPermission(i.requiredPermission)
  );

  const visibleMoreItems = moreMenuItems.filter(
    (i) => !i.requiredPermission || hasPermission(i.requiredPermission)
  );

  // Check if user has multiple channels (can switch stores)
  const canSwitchStores = user?.channels && user.channels.length > 1;

  // Store identity from tenant data
  const [tenant, setTenant] = useState<StoreIndicatorTenant | null>(null);
  const store = resolveStoreIndicator(
    tenant,
    activeChannel?.code === '__default_channel__' ? t('channel.default') : (activeChannel?.code ?? ''),
  );
  const storeUrl = store.domain;

  // Language state for profile toggle
  const [currentLang, setCurrentLang] = useState<'ar' | 'en'>('ar');

  useEffect(() => {
    if (typeof window === 'undefined') return;
    const saved = localStorage.getItem(DASHBOARD_LANGUAGE_STORAGE_KEY) as 'ar' | 'en' | null;
    if (saved && (saved === 'ar' || saved === 'en')) {
      setCurrentLang(saved);
    }
  }, []);

  useEffect(() => {
    if (!activeChannel) return;
    setTenant(null);
    const fetchTenant = async () => {
      try {
        const client = getClient();
        const result = await client.query(GET_CURRENT_TENANT, {}).toPromise();
        setTenant(result.data?.currentTenant ?? null);
      } catch { /* ignore */ }
    };
    fetchTenant();
  }, [activeChannel]);

  const toggleLanguage = () => {
    const newLang = currentLang === 'ar' ? 'en' : 'ar';
    localStorage.setItem(DASHBOARD_LANGUAGE_STORAGE_KEY, newLang);
    document.cookie = `${DASHBOARD_LANGUAGE_STORAGE_KEY}=${newLang};path=/;max-age=31536000;SameSite=Lax`;
    document.documentElement.lang = newLang;
    document.documentElement.dir = newLang === 'ar' ? 'rtl' : 'ltr';
    window.location.reload();
  };

  return (
    <header className="sticky top-0 z-40 w-full border-b border-border/80 bg-card/95 backdrop-blur-xl supports-[backdrop-filter]:bg-card/90">
      <div className="mx-auto flex h-[3.75rem] max-w-[1540px] items-center gap-3 px-4 lg:px-7">
        {/* Logo */}
        <Link
          href="/"
          className="flex h-10 shrink-0 items-center rounded-lg px-1 outline-none focus-visible:ring-2 focus-visible:ring-ring"
          aria-label={currentLang === 'ar' ? 'تالي — الرئيسية' : 'TALI — Home'}
        >
          <TaliLogo
            language={currentLang}
            className={currentLang === 'ar' ? 'h-9' : 'h-8'}
          />
        </Link>

        {/* Main Navigation */}
        <nav className="mx-1 flex min-w-0 items-center gap-1">
          {visibleNavItems.map((item) => {
            const isActive = isMainNavActive(pathname, item.href);
            const Icon = item.icon;
            const directJumps = getTopNavMenuItems(item.href).filter(
              candidate => visibleDirectJumpHrefs.has(candidate.href),
            );
            return (
              <div
                key={item.href}
                className={cn(
                  'relative flex h-10 items-center rounded-lg text-base font-medium transition-colors duration-200',
                  isActive
                    ? 'bg-brand-tint text-brand-strong'
                    : 'text-muted-foreground hover:bg-accent hover:text-accent-foreground'
                )}
              >
                <Link
                  href={item.href}
                  aria-current={isActive ? 'page' : undefined}
                  className={cn(
                    'flex h-full min-w-0 items-center gap-2 ps-2.5',
                    directJumps.length > 1 ? 'pe-1' : 'pe-2.5',
                  )}
                >
                  <Icon />
                  <span className="hidden xl:inline">{t(item.labelKey)}</span>
                  {item.href === '/orders' && ordersBadge > 0 && (
                    <Badge variant="destructive" className="min-w-[18px] justify-center px-1 text-[10px]">
                      {ordersBadge > 9 ? '9+' : ordersBadge}
                    </Badge>
                  )}
                </Link>

                {directJumps.length > 1 && (
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <button
                        type="button"
                        className="hidden h-8 items-center rounded-md px-1 text-muted-foreground outline-none hover:bg-accent hover:text-foreground focus-visible:ring-2 focus-visible:ring-ring xl:flex"
                        aria-label={`${t(item.labelKey)} — ${t('common.more')}`}
                      >
                        <ChevronDown />
                      </button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="start" className="w-60 rounded-xl border-border bg-card p-1.5">
                      <DropdownMenuGroup>
                        {directJumps.map(candidate => {
                          const CandidateIcon = candidate.icon;
                          const candidateActive = isSidebarItemActive(
                            pathname,
                            candidate,
                            directJumps,
                          );
                          return (
                            <DropdownMenuItem key={candidate.href} asChild className="rounded-lg">
                              <Link
                                href={candidate.href}
                                className={cn(
                                  'flex w-full items-center gap-2',
                                  candidateActive && 'bg-accent font-medium text-primary',
                                )}
                              >
                                <CandidateIcon />
                                <span>{t(candidate.labelKey)}</span>
                              </Link>
                            </DropdownMenuItem>
                          );
                        })}
                      </DropdownMenuGroup>
                    </DropdownMenuContent>
                  </DropdownMenu>
                )}

              </div>
            );
          })}

          {/* More Dropdown */}
          {visibleMoreItems.length > 0 && (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button className="flex h-10 items-center gap-2 rounded-lg px-2.5 text-base font-medium text-muted-foreground transition-colors hover:bg-accent hover:text-accent-foreground">
                  <MoreHorizontal className="size-[18px]" />
                  <span className="hidden xl:inline">{t('common.more')}</span>
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="start" className="w-52 rounded-xl border-border bg-card p-1.5">
                <DropdownMenuGroup>
                  {visibleMoreItems.map((item) => {
                    const Icon = item.icon;
                    const isActive = isMainNavActive(pathname, item.href);
                    return (
                      <DropdownMenuItem key={item.href} asChild className="rounded-lg">
                        <Link
                          href={item.href}
                          className={cn(
                            'flex w-full items-center gap-2',
                            isActive && 'bg-accent text-primary font-medium'
                          )}
                        >
                          <Icon className="w-4 h-4" />
                          <span>{t(item.labelKey)}</span>
                        </Link>
                      </DropdownMenuItem>
                    );
                  })}
                </DropdownMenuGroup>
              </DropdownMenuContent>
            </DropdownMenu>
          )}
        </nav>

        {/* Spacer */}
        <div className="flex-1" />

        <GlobalCommandMenu />

        {/* Store Indicator */}
        {activeChannel && (
          canSwitchStores ? (
            <Link
              href="/select-store?switch=1"
              className="flex h-9 shrink-0 items-center gap-1.5 rounded-lg border border-border bg-card px-2.5 text-sm font-medium text-muted-foreground transition-colors hover:border-primary/30 hover:text-foreground"
              title={storeUrl ? `${t('channel.switchStore')} · ${storeUrl}` : t('channel.switchStore')}
            >
              <span
                aria-hidden="true"
                className="flex size-5 shrink-0 items-center justify-center rounded-md bg-primary text-xs font-bold text-primary-foreground"
              >
                {store.initial}
              </span>
              <span className="max-w-40 truncate text-sm font-semibold text-foreground">{store.name}</span>
              {store.isTrial && <Badge variant="info" className="h-5 px-2">{t('storeUrl.trial')}</Badge>}
            </Link>
          ) : (
            <a
              href={storeUrl ? `https://${storeUrl}` : '#'}
              target={storeUrl ? "_blank" : undefined}
              rel={storeUrl ? "noopener noreferrer" : undefined}
              className={cn(
                "flex h-9 shrink-0 items-center gap-1.5 rounded-lg border border-border bg-card px-2.5 text-sm font-medium text-muted-foreground transition-colors",
                storeUrl && "cursor-pointer hover:border-primary/30 hover:text-foreground"
              )}
              title={storeUrl ? `${t('storeUrl.viewStore')} · ${storeUrl}` : undefined}
              onClick={(e) => {
                if (!storeUrl) e.preventDefault();
              }}
            >
              <span
                aria-hidden="true"
                className="flex size-5 shrink-0 items-center justify-center rounded-md bg-primary text-xs font-bold text-primary-foreground"
              >
                {store.initial}
              </span>
              <span className="max-w-40 truncate text-sm font-semibold text-foreground">{store.name}</span>
              {store.isTrial && <Badge variant="info" className="h-5 px-2">{t('storeUrl.trial')}</Badge>}
              {storeUrl && <ExternalLink className="size-3 text-muted-foreground" />}
            </a>
          )
        )}

        {/* Notification Bell */}
        <NotificationBell />

        {/* User Menu */}
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <button className="flex items-center rounded-full outline-none ring-offset-background focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2">
              <Avatar className="size-9 border border-border">
                <AvatarImage src="/avatar.png" />
                <AvatarFallback className="bg-primary text-sm text-primary-foreground">
                  {user?.identifier?.charAt(0).toUpperCase() || 'U'}
                </AvatarFallback>
              </Avatar>
            </button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="w-64">
            {/* User info */}
            <div className="px-2 py-1.5">
              <p className="text-sm font-medium">{user?.identifier || 'User'}</p>
            </div>
            <DropdownMenuSeparator />

            {/* Profile */}
            <DropdownMenuItem asChild>
              <Link href="/settings/profile" className="flex items-center gap-2 w-full">
                <User className="w-4 h-4" />
                <span>{t('navigation.profile')}</span>
              </Link>
            </DropdownMenuItem>

            {/* Security / 2FA */}
            <DropdownMenuItem asChild>
              <Link href="/settings/security" className="flex items-center gap-2 w-full">
                <Shield className="w-4 h-4" />
                <span>{t('navigation.security')}</span>
              </Link>
            </DropdownMenuItem>

            <DropdownMenuSeparator />

            {/* Store URL */}
            {storeUrl && (
              <>
                <div className="px-2 py-2">
                  <p className="text-xs text-muted-foreground mb-1.5">{t('storeUrl.storeAddress')}</p>
                  <div className="flex items-center gap-1 px-2 py-1.5 text-sm bg-muted rounded-md border border-border">
                    <span className="text-muted-foreground truncate flex-1 text-xs" dir="ltr">{storeUrl}</span>
                    <button
                      onClick={() => {
                        navigator.clipboard.writeText(`https://${storeUrl}`);
                        toast.success(t('storeUrl.copied'));
                      }}
                      className="p-1 hover:bg-accent rounded transition-colors shrink-0"
                      title={t('storeUrl.copyLink')}
                    >
                      <Copy className="w-3.5 h-3.5 text-muted-foreground" />
                    </button>
                    <button
                      onClick={() => window.open(`https://${storeUrl}`, '_blank')}
                      className="p-1 hover:bg-accent rounded transition-colors shrink-0"
                      title={t('storeUrl.viewStore')}
                    >
                      <ExternalLink className="w-3.5 h-3.5 text-muted-foreground" />
                    </button>
                  </div>
                </div>
                <DropdownMenuSeparator />
              </>
            )}

            {/* Theme Toggle */}
            <div className="px-2 py-1.5">
              <p className="text-xs text-muted-foreground mb-1.5">{t('common.theme')}</p>
              <div className="flex items-center gap-1">
                <button
                  onClick={() => setMode('light')}
                  className={cn(
                    'flex-1 flex items-center justify-center gap-1 px-2 py-1.5 text-xs rounded-md transition-colors',
                    mode === 'light' ? 'bg-primary/10 text-primary' : 'hover:bg-muted text-muted-foreground'
                  )}
                  title={t('common.lightMode')}
                >
                  <Sun className="w-3.5 h-3.5" />
                  <span>{t('common.light')}</span>
                </button>
                <button
                  onClick={() => setMode('dark')}
                  className={cn(
                    'flex-1 flex items-center justify-center gap-1 px-2 py-1.5 text-xs rounded-md transition-colors',
                    mode === 'dark' ? 'bg-primary/10 text-primary' : 'hover:bg-muted text-muted-foreground'
                  )}
                  title={t('common.darkMode')}
                >
                  <Moon className="w-3.5 h-3.5" />
                  <span>{t('common.dark')}</span>
                </button>
                <button
                  onClick={() => setMode('system')}
                  className={cn(
                    'flex-1 flex items-center justify-center gap-1 px-2 py-1.5 text-xs rounded-md transition-colors',
                    mode === 'system' ? 'bg-primary/10 text-primary' : 'hover:bg-muted text-muted-foreground'
                  )}
                  title={t('common.systemMode')}
                >
                  <Monitor className="w-3.5 h-3.5" />
                  <span>{t('common.system')}</span>
                </button>
              </div>
            </div>

            <DropdownMenuSeparator />

            {/* Language Toggle */}
            <DropdownMenuItem onClick={toggleLanguage} className="flex items-center gap-2">
              <Globe className="w-4 h-4" />
              <span>{currentLang === 'ar' ? 'English' : 'عربي'}</span>
            </DropdownMenuItem>

            <DropdownMenuSeparator />

            {/* Logout */}
            <DropdownMenuItem onClick={() => logout()} className="flex items-center gap-2 text-red-600 focus:text-red-600">
              <LogOut className="w-4 h-4" />
              <span>{t('common.logout')}</span>
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>
    </header>
  );
}
